import {coordsToKey} from "../coords/coordsToKey";
import {SUPPLY_UNPASSABLE_TOKENS} from "../../configs/supply";
import {MAX_SUPPLY_DISTANCE} from "../../constants/supply";
import {checkIfHexBlockedByEnemyUnit} from "./checkIfHexBlockedByEnemyUnit";
import {COMMON_BUILDING_TOKENS, TOKENS} from "../../constants/tokens";
import {HEX_DIRECTIONS} from "../../constants/geometry";
import {isPlayerPolicy} from "../factions/isPlayerPolicy";
import {POLICIES} from "../../constants/policies";

/**
 * Проверяет, является ли токен блокирующим для снабжения (горы, вода)
 * @param {string|null} hexToken - токен ячейки
 * @returns {boolean}
 */
const isTokenSupplyBlocker = (hexToken) => {
  return SUPPLY_UNPASSABLE_TOKENS.includes(hexToken);
};

/**
 * Вычисляет доступные для снабжения ячейки с отрядами игрока
 * Путь снабжения может проходить только по определённым типам ячеек
 * @param {Object} hexGridRenderer - рендер гексагональной сетки
 * @param {Object} hexes - состояние ячеек
 * @param specialBuildings
 * @param {Array} units - все отряды
 * @param {string} farmHexKey - ключ ячейки с фермой
 * @param currentPlayer
 * @param {Object} currentPlayerCities - массив городов игрока
 * @param buildingsDamage
 * @returns {Array} массив ключей ячеек с отрядами игрока, которые можно снабдить
 */
export const calculateAvailableSupplyCells = (
  hexGridRenderer,
  hexes,
  specialBuildings,
  units,
  farmHexKey,
  currentPlayer,
  currentPlayerCities,
  buildingsDamage,
) => {
  if (isPlayerPolicy(currentPlayer, POLICIES.ECOANARCHISM)) {
    return units
      .filter(u => u.ownerId === currentPlayer.id)
      .map(unit => unit.hexKey)
  }

  // Находим все вражеские отряды. Нанятые крысолюдами за снабжение еще не
  // появились на поле и пути не перекрывают (см. checkIfHexBlockedByEnemyUnit)
  const enemyUnits = units.filter(u => u.ownerId !== currentPlayer.id && !u.isSupplyRecruit);

  // Создаём Set координат вражеских отрядов
  const enemyUnitHexKeys = new Set(enemyUnits.map(u => u.hexKey));

  // Создаём Set координат ферм игрока из его городов
  const playerFarmKeysSet = new Set();
  currentPlayerCities.forEach(city => {
    const farms = city[COMMON_BUILDING_TOKENS.FARM] || [];

    farms.forEach(farmKey => {
      playerFarmKeysSet.add(farmKey);
    });
  });

  const adjacentToEnemy = new Set();
  enemyUnitHexKeys.forEach(enemyKey => {
    const [eq, er] = enemyKey.split(',').map(Number);
    for (const [dq, dr] of HEX_DIRECTIONS) {
      const adjKey = coordsToKey(eq + dq, er + dr);
      adjacentToEnemy.add(adjKey);
    }
  });

  // Множество доступных ячеек (с отрядами игрока)
  const reachableUnitHexKeys = new Set();

  // Посещённые фермы для цепочки (чтобы не зациклить)
  const visitedFarms = new Set();

  // Посещённые ячейки для предотвращения зацикливания (в рамках одного сегмента)
  let visited = new Set();

  const [startQ, startR] = farmHexKey.split(',').map(Number);

  // Проверяем: есть ли отряд игрока на самой ферме
  // Если да - это допустимая точка снабжения (отряд на ферме может получить снабжение)
  const unitOnFarm = units.find(u => u.hexKey === farmHexKey && u.ownerId === currentPlayer.id);
  if (unitOnFarm) {
    reachableUnitHexKeys.add(farmHexKey);
  }

  /**
   * DFS для поиска пути снабжения от текущей фермы
   * @param {Array} startHexes - начальные ячейки для поиска
   * @param {number} maxDepth - максимальная глубина поиска
   * @param {Set} localVisited - локальный набор посещённых ячеек
   */
  const dfsFromFarm = (startHexes, maxDepth, localVisited) => {
    const newReachableCells = [];

    for (const [q, r] of startHexes) {
      for (const [dq, dr] of HEX_DIRECTIONS) {
        const newQ = q + dq;
        const newR = r + dr;
        const newKey = coordsToKey(newQ, newR);

        // Проверяем, что ячейка существует на поле
        if (!hexGridRenderer.hexes[newKey]) {
          continue;
        }

        // Пропускаем если уже посещали
        if (localVisited.has(newKey)) {
          continue;
        }
        localVisited.add(newKey);

        // Проверяем содержимое ячейки
        const hexToken = hexes[newKey];

        // Проверяем, является ли ячейка блокирующей (гора, вода)
        if (isTokenSupplyBlocker(hexToken)) {
          continue;
        }

        // Проверяем, находится ли ячейка рядом с вражеским отрядом
        if (adjacentToEnemy.has(newKey) && !enemyUnitHexKeys.has(newKey)) {
          const unitOnCell = units.find(u => u.hexKey === newKey);
          const isPlayerUnit = unitOnCell && unitOnCell.ownerId === currentPlayer.id;

          // Если ячейка смежна отряду противника, то можно снабдить отряд в этой ячейке,
          // Но нельзя продолжить из нее путь снабжения к другим ячейкам.
          if (isPlayerUnit) {
            reachableUnitHexKeys.add(newKey);
          }

          continue;
        }

        const unitOnCell = units.find(u => u.hexKey === newKey);
        const isPlayerUnit = unitOnCell && unitOnCell.ownerId === currentPlayer.id;

        // Если это не конечная точка с отрядом игрока - проверяем токен
        if (!isPlayerUnit) {
          // Пустая ячейка на пути - нельзя
          if (!hexToken) {
            continue;
          }
          // Проверяем проходимость токена
          if (isTokenSupplyBlocker(hexToken)) {
            continue;
          }
        }

        // Если на ячейке вражеский отряд - непроходимо
        if (unitOnCell && unitOnCell.ownerId !== currentPlayer.id && !unitOnCell.isSupplyRecruit) {
          continue;
        }

        // Проверяем: можно ли использовать ячейку для продолжения пути
        const isEmptyWithPlayerUnit = !hexToken && unitOnCell && unitOnCell.ownerId === currentPlayer.id;

        // Если это союзный отряд на пустой ячейке - это может быть только конечной точкой
        if (isEmptyWithPlayerUnit) {
          if (isPlayerUnit) {
            reachableUnitHexKeys.add(newKey);
          }
          continue;
        }

        // Если на ячейке есть отряд игрока, добавляем в результат
        if (isPlayerUnit) {
          reachableUnitHexKeys.add(newKey);
        }

        // ПРОВЕРЯЕМ: это ферма игрока - запускаем новую цепочку
        // Важно: ферма должна принадлежать игроку!
        if (hexToken === COMMON_BUILDING_TOKENS.FARM && playerFarmKeysSet.has(newKey)) {
          // Проверяем, не заблокирована ли ферма вражеским отрядом
          const isBlocked = checkIfHexBlockedByEnemyUnit(newKey, TOKENS.FARM, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage);

          if (!isBlocked && !visitedFarms.has(newKey)) {
            // Это новая ферма в цепочке - добавляем её
            visitedFarms.add(newKey);

            // Проверяем, есть ли отряд игрока на этой ферме
            const unitOnThisFarm = units.find(u => u.hexKey === newKey && u.ownerId === currentPlayer.id);
            if (unitOnThisFarm) {
              reachableUnitHexKeys.add(newKey);
            }

            // Запускаем новый DFS от этой фермы с чистым набором посещённых ячеек
            // (сбрасываем глубину)
            const newLocalVisited = new Set();
            newLocalVisited.add(newKey);
            dfsFromFarm([[newQ, newR]], MAX_SUPPLY_DISTANCE, newLocalVisited);
          }
        }

        // Продолжаем поиск в пределах текущей глубины
        if (maxDepth > 1) {
          newReachableCells.push([newQ, newR]);
        }
      }
    }

    if (newReachableCells.length > 0 && maxDepth > 1) {
      dfsFromFarm(newReachableCells, maxDepth - 1, localVisited);
    }
  };

  // Запускаем DFS с позиции фермы
  visited.add(farmHexKey);
  visitedFarms.add(farmHexKey);
  dfsFromFarm([[startQ, startR]], MAX_SUPPLY_DISTANCE, visited);

  return [...reachableUnitHexKeys];
};
